import Search from '@phoenix/search/interfaces/Search';
import { get, isBoolean, isEmpty, isString, omit } from 'lodash-es';
import { algoliasearch } from 'algoliasearch';
import type { Hit, SearchClient, SearchQuery, SearchResponse, SearchResponses } from 'algoliasearch';
import { TAB_ALL } from '@maison/helpers/search-helper';
import SearchData from '@phoenix/search/interfaces/SearchData';
import AlgoliaData from '@module/Algolia/resources/js/search/interfaces/AlgoliaData';
import {
  SearchOptions,
  SearchQueryWrapper,
  SearchSettings,
  Transformer,
} from '@module/Algolia/resources/js/search/types';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import { transformArticle, transformProduct, transformStore } from '@module/Algolia/resources/js/search/transformers';
import { transformFacets } from '@module/Algolia/resources/js/search/facets';
import { formatUrl, getData } from '@module/Algolia/resources/js/search/helpers';
import { AggregatedDataResult, SearchResult } from '@phoenix/search/interfaces/SearchResultTypes';
import SuggestionData from '@phoenix/search/interfaces/SuggestionData';
import algoliaInsights from '@module/Algolia/resources/js/search/AlgoliaInsights';
import { isMobile } from '@phoenix/helpers/common-helper';

let instance: AlgoliaSearch | null = null;

export enum IndexNames {
  Products = 'products',
  Collections = 'collections',
  Suggestions = 'suggestions',
  Articles = 'articles',
  Stores = 'stores',
}

export default class AlgoliaSearch implements Search {
  protected indexPrefix: string;

  private client: SearchClient | null = null;

  constructor() {
    this.indexPrefix = window.Phoenix.config.ALGOLIA.indexPrefix;
  }

  protected get searchClient(): SearchClient {
    if (!this.client) {
      this.client = algoliasearch(window.Phoenix.config.ALGOLIA.appId, window.Phoenix.config.ALGOLIA.apiKey);
    }

    return this.client;
  }

  static getInstance(): AlgoliaSearch {
    if (!instance) {
      instance = new AlgoliaSearch();
    }

    return instance;
  }

  protected getMainSearchSettings(): SearchSettings[] {
    return [
      this.getProductsSettings(),
      this.getStoresSettings(),
      this.getArticlesSettings(),
      this.getMatchSpecificProductSettings(),
    ];
  }

  protected getSettings(name: string, additionalSettings: SearchSettings): SearchSettings {
    const settings = this[`get${name[0].toUpperCase() + name.slice(1)}Settings`]?.() ?? {};
    if (settings.filters || additionalSettings.filters) {
      settings.filters = { ...settings.filters, ...additionalSettings.filters };
    }

    return {
      ...settings,
      ...omit(additionalSettings, ['filters']),
    };
  }

  protected getProductsSettings(): SearchSettings {
    return {
      name: 'products',
      type: 'search',
      withFacets: true,
      stickyFacets: true,
      indexName: IndexNames.Products,
      transformer: transformProduct,
      hasAnalytics: true,
      hitsPerPage: 24,
      analyticsTags: ['Products'],
    };
  }

  protected getStoresSettings(): SearchSettings {
    return {
      name: 'stores',
      type: 'search',
      indexName: IndexNames.Stores,
      transformer: transformStore,
      hasAnalytics: true,
      hitsPerPage: 24,
      analyticsTags: ['Stores'],
    };
  }

  protected getArticlesSettings(): SearchSettings {
    const contentType = 'Editorial';
    return {
      name: 'articles',
      type: 'search',
      indexName: IndexNames.Articles,
      transformer: transformArticle,
      hasAnalytics: true,
      hitsPerPage: 24,
      filters: {
        contentType,
      },
      analyticsTags: [contentType],
    };
  }

  protected getServicesSettings(): SearchSettings {
    const contentType = 'Services';
    return {
      name: 'services',
      type: 'search',
      indexName: IndexNames.Articles,
      transformer: transformArticle,
      hasAnalytics: true,
      hitsPerPage: 24,
      filters: {
        contentType,
      },
      analyticsTags: [contentType],
    };
  }

  protected getSuggestionsSettings(maxHits = 3): SearchSettings {
    return {
      name: 'suggestions',
      type: 'search',
      indexName: `main_query_suggestions_${window.Phoenix.laravelLanguage}`,
      hitsPerPage: maxHits,
      noFilter: true,
      transformer: (data: AlgoliaData): SuggestionData => ({ key: data.query as string, value: data.query as string }),
    };
  }

  protected getMatchSpecificProductSettings(): SearchSettings {
    return {
      name: 'matchSpecificProduct',
      type: 'search',
      indexName: IndexNames.Products,
      transformer: transformProduct,
      hitsPerPage: 1,
      activations: ['hidden', 'visible'],
      disableTracking: true,
      filters: {
        allReferences: '{query}',
      },
    };
  }

  static isEnabled(): boolean {
    return window.Phoenix.config.ALGOLIA.enabled && getFeatureFlag(FEATURE_FLAG.ALGOLIA);
  }

  isAnalyticsEnabled(): boolean {
    return false;
  }

  getSortIndexName(indexName: string, sort?: string): string | null {
    return sort ? get(window.Phoenix.config.ALGOLIA.indexes, [indexName, 'sorts', sort]) : null;
  }

  async performSearch(query: string, options?: SearchOptions, analyticsTags: string[] = []): Promise<SearchResult> {
    options = {
      settings: [],
      ruleContexts: [],
      ...options,
    };

    options.page = options.page ? options.page - 1 : 0;

    const globalAnalyticsTags = [isMobile() ? 'mobile' : 'desktop', ...analyticsTags];

    const queries: SearchQueryWrapper[] = options.settings.flatMap((settings: SearchSettings, index: number) => {
      options.settings[index] = settings = this.getSettings(settings.name, settings);

      const searchQuery: SearchQueryWrapper = {
        query: {
          query,
          indexName: this.getFullIndexName(
            this.getSortIndexName(settings.indexName, settings.sort) ?? settings.indexName,
          ),
          analytics: !(settings.disableTracking ?? false),
          clickAnalytics: !!(this.isAnalyticsEnabled() && settings.hasAnalytics),
          hitsPerPage: settings.hitsPerPage,
          filters: settings.noFilter ? '' : this.getFilters(settings.filters, settings.activations, query),
          page: options.page,
          facets: settings.withFacets ? ['*'] : [],
          ruleContexts: [
            `country-${window.Phoenix.currentCountry}`,
            `localisation-${window.Phoenix.localisationUrlCode}`,
            ...(options.ruleContexts ?? []),
          ],
          analyticsTags: [
            ...globalAnalyticsTags,
            ...(options.analyticsTags ?? []),
            ...(settings.analyticsTags ?? []),
            `language-${window.Phoenix.laravelLanguage}`,
            `country-${window.Phoenix.currentCountry}`,
            `localisation-${window.Phoenix.localisationUrlCode}`,
          ],
        },
        settings,
      };

      if (!settings.stickyFacets || !settings.withFacets || isEmpty(settings.filters)) {
        return [searchQuery];
      }

      const stickyFacetsQueries: SearchQueryWrapper[] = this.getStickyFacetsQueries(settings, searchQuery);

      return [searchQuery, ...stickyFacetsQueries];
    });

    const response = await this.performMultiIndexSearch(Object.values(queries).map((query) => query.query));
    const results = response.results as SearchResponse<AlgoliaData>[];

    results.forEach((result, index) => {
      queries[index].result = result;
    });

    this.handleStickyFacets(queries);

    const searchQueries = queries.filter((query) => query.settings.type === 'search');

    const redirectUrl = this.getRedirectUrl(searchQueries);
    if (redirectUrl !== '') {
      return {
        type: 'redirect',
        redirectUrl,
      };
    }

    if (searchQueries.length === 1) {
      const query = queries[0];
      if (query.settings.hasAnalytics) {
        this.handleAnalytics(query.result.hits, query.settings.indexName);
      }
      return {
        type: 'data',
        data: this.transformHits(query.settings.transformer, query.result.hits, query.result.queryID),
        filters: transformFacets(query.settings.indexName, query.result),
        meta: {
          total: query.result.nbHits,
          current_page: query.result.page + 1,
          last_page: query.result.nbPages,
        },
      };
    }

    const returnData: AggregatedDataResult = {
      type: 'aggregated',
      results: {},
    };

    searchQueries.reduce((carry, query) => {
      carry[query.settings.name] = {
        data: this.transformHits(query.settings.transformer, query.result.hits, query.result.queryID),
        filters: transformFacets(query.settings.indexName, query.result),
        total: query.result.nbHits,
        meta: {
          total: query.result.nbHits,
          current_page: query.result.page + 1,
          last_page: query.result.nbPages,
        },
      };
      if (query.settings.hasAnalytics) {
        this.handleAnalytics(query.result.hits, query.settings.indexName);
      }
      return carry;
    }, returnData.results);

    return returnData;
  }

  protected getStickyFacetsQueries(settings: SearchSettings, searchQuery: SearchQueryWrapper): SearchQueryWrapper[] {
    const sharedSettings = {
      attributesToRetrieve: [],
      maxValuesPerFacet: 100,
      hitsPerPage: 0,
      ruleContexts: searchQuery.query.ruleContexts,
    };

    const disjunctiveFacetsQueries: SearchQueryWrapper[] =
      Object.keys(settings.filters).map((facet) => {
        // filters is a proxy object, we need to clone it to avoid mutating the original object (structuredClone does not work)
        const filters = JSON.parse(JSON.stringify(settings.filters));

        delete filters[facet];

        return {
          query: {
            indexName: searchQuery.query.indexName,
            analytics: false,
            query: searchQuery.query.query,
            filters: this.getFilters(filters, settings.activations, searchQuery.query.query),
            facets: [this.getAlgoliaFacetName(facet)],
            ...sharedSettings,
          },
          settings: {
            ...settings,
            type: 'facet',
          },
        };
      }) ?? [];

    const allFacets: SearchQueryWrapper = {
      query: {
        indexName: searchQuery.query.indexName,
        analytics: false,
        query: searchQuery.query.query,
        filters: this.getFilters({}, settings.activations, searchQuery.query.query),
        facets: ['*'],
        ...sharedSettings,
      },
      settings: {
        ...settings,
        type: 'allFacets',
      },
    };

    return [allFacets, ...disjunctiveFacetsQueries];
  }

  protected handleStickyFacets(queries: SearchQueryWrapper[]) {
    queries.forEach((query) => {
      if (query.settings.type !== 'search' || !query.settings.stickyFacets) {
        return;
      }

      const allFacets = queries.find(
        (subQuery) =>
          subQuery.settings.type === 'allFacets' && subQuery.settings.indexName === query.settings.indexName,
      )?.result?.facets;
      const facets = queries
        .filter(
          (subQuery) => subQuery.settings.type === 'facet' && subQuery.settings.indexName === query.settings.indexName,
        )
        .reduce((carry, subQuery) => ({ ...carry, ...subQuery.result.facets }), {});
      const partialFacets = {
        ...query.result.facets,
        ...facets,
      };

      if (!isEmpty(allFacets)) {
        const stickyFacets = Object.entries(allFacets).reduce((carryFacets, [facet, facetItems]) => {
          const emptyItems = Object.entries(facetItems).reduce((carryItems, [key]) => {
            //If the facet value is not override by partialFacets, it means that the facet value is empty
            carryItems[key] = 0;
            return carryItems;
          }, {});

          carryFacets[facet] = {
            ...emptyItems,
            ...partialFacets[facet],
          };
          return carryFacets;
        }, {});

        query.result.facets = stickyFacets;
      }
    });
  }

  protected handleAnalytics(items: AlgoliaData[], index: string) {
    if (!this.isAnalyticsEnabled()) {
      return;
    }
    algoliaInsights.handleViews(
      items.map((item) => item.objectID as string),
      index,
      'Items viewed',
    );
  }

  async performMainSearch(query: string, tab: string = TAB_ALL, options?: SearchOptions): Promise<SearchResult> {
    if (tab === TAB_ALL) {
      options = {
        ...options,
        settings: this.getMainSearchSettings(),
      };
    } else {
      options = {
        ...options,
        settings: [
          this.getSettings(tab, {
            ...get(options, ['settings', 0]),
          }),
        ],
      };
    }

    const result = await this.performSearch(query, options, ['search']);
    if (result.type === 'aggregated') {
      result.results = omit(result.results, this.getMatchSpecificProductSettings().name);
    }

    return result;
  }

  async fetchQuerySuggestions(query: string): Promise<SearchData[]> {
    const result = await this.performSearch(query, {
      settings: [this.getSuggestionsSettings()],
    });

    if (result.type === 'data') {
      return result.data;
    }

    return [];
  }

  protected async performMultiIndexSearch(queries: SearchQuery[]): Promise<SearchResponses<AlgoliaData>> {
    return this.searchClient.search({ requests: queries });
  }

  protected transformHits(transformer: Transformer, hits: Hit<AlgoliaData>[], queryID = ''): SearchData[] {
    return hits
      .map((item: AlgoliaData) => ({ ...item, _queryID: queryID }))
      .map((item) => ({
        ...transformer(item),
        _objectID: item.objectID,
        _queryID: queryID,
      }));
  }

  public getFullIndexName(index: string): string {
    return `${this.indexPrefix}${index}`;
  }

  protected getAlgoliaFacetName(facet: string): string {
    return facet.replaceAll('--', '.');
  }

  protected getFilters(filters: object = {}, activations = ['visible'], query = ''): string {
    filters = JSON.parse(JSON.stringify(filters));

    const currentTimestamp = Math.floor(new Date().getTime() / 1000);

    if (activations.length > 0) {
      filters[`activations.${window.Phoenix.localisationUrlCode}`] = activations;
    }

    const newFilters = Object.keys(filters)
      .filter((attribute) => filters[attribute] !== '')
      .map((attribute) => {
        const key = this.getAlgoliaFacetName(attribute);
        let value = filters[attribute];

        if (Array.isArray(value)) {
          // For now, we can assume that we want to filter on a range
          // if the value has 2 items and the items are both numbers
          if (value.length === 2 && value.every((value) => typeof value === 'number')) {
            return `${key}:${this.formatFilterValue(value[0])} TO ${this.formatFilterValue(value[1])}`;
          }

          value = value.map((value) => `${key}:${this.formatFilterValue(value, query)}`).join(' OR ');
          return `(${value})`;
        }

        value = this.formatFilterValue(value, query);
        return `${key}:${value}`;
      });

    newFilters.push(`publicationDateTimestamp <= ${currentTimestamp}`);

    return newFilters.join(' AND ');
  }

  protected formatFilterValue(value: boolean | string | number, query = ''): string | number {
    if (isBoolean(value)) {
      return value ? 'true' : 'false';
    }

    if (isString(value)) {
      return `'${value === '{query}' ? query : value}'`;
    }

    return value;
  }

  protected getRedirectUrl(queries: SearchQueryWrapper[]): string {
    let redirectUrl = queries.reduce(
      (carry, query) => (carry !== '' ? carry : get(query.result, ['renderingContent', 'redirect', 'url'], '')),
      '',
    );

    if (redirectUrl === '') {
      const matchSpecificProductQuery: SearchQueryWrapper = queries.find(
        (query) => query.settings.name === 'matchSpecificProduct',
      );

      if (matchSpecificProductQuery?.result?.nbHits > 0) {
        redirectUrl = this.getMatchSpecificProductUrl(get(matchSpecificProductQuery.result, ['hits', 0]));
      }
    }

    return formatUrl(redirectUrl);
  }

  protected getMatchSpecificProductUrl(hit?: AlgoliaData): string {
    return getData(get(hit, 'url', ''), '');
  }
}

export const algoliaSearch = AlgoliaSearch.getInstance();
